import { useEffect, useState } from "react";
import CrudSection from "../components/CrudSection";
import FeatureDashboard, { type RecentItem } from "../../../components/base/FeatureDashboard";
import { StatusTextBadge } from "../../../components/base/StatusTextBadge";
import { LeadsImportModal } from "../components/LeadsImportModal";
import EmpresasLive from "./EmpresasLive";
import ContactosLive from "./ContactosLive";
import { buildThemeClasses } from "../theme";
import { fetchLeads, leadDate, type Lead, type LeadStatus } from "../services/leadsService";
import { fetchCompanies, type Company } from "../services/companiesService";
import { fetchContacts } from "../services/contactsService";
import { fetchCompaniesUniverse, type UniverseTotals } from "../services/companyUniverseService";
import type { CrudColumn, CrudField, DashboardChartPoint } from "../types";

// ===========================================================================
// Contactos y Leads — unified module. The dashboard blends LIVE lead metrics
// (real prospects captured from the landing contact form) with the contact /
// company directory, the way HubSpot groups everything under "Contacts". The
// directory CRUD sections below are still demo scaffolds (in-session data).
// ===========================================================================

// Map a raw lead `source` value to a friendly bucket for the origin chart.
const SOURCE_LABEL: Record<string, string> = {
  landing: "Landing",
  referido: "Referido",
  referral: "Referido",
  ads: "Ads",
  evento: "Evento",
  event: "Evento",
  outbound: "Outbound",
};

const STATUS_RECENT: Record<LeadStatus, { badge: string; tone: RecentItem["tone"] }> = {
  new: { badge: "Nuevo", tone: "ok" },
  contacted: { badge: "Contactado", tone: "warn" },
  qualified: { badge: "Calificado", tone: "info" },
  unqualified: { badge: "No calificado", tone: "muted" },
  converted: { badge: "Convertido", tone: "ok" },
  lost: { badge: "Perdido", tone: "danger" },
};

function relTime(value: Lead["createdAt"]): string {
  const d = leadDate(value);
  if (!d) return "";
  const min = Math.floor((Date.now() - d.getTime()) / 60000);
  if (min < 1) return "hace un momento";
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  return `hace ${Math.floor(h / 24)} d`;
}

const SHORTCUT_RECENT: RecentItem[] = [
  { primary: "Bandeja de leads", secondary: "Prospectos en vivo desde la landing", badge: "Live", tone: "ok" },
  { primary: "Directorio de contactos", secondary: "Personas y empresas de tu CRM", badge: "Directorio", tone: "info" },
  { primary: "Segmentos", secondary: "Listas dinámicas de contactos", badge: "Demo", tone: "muted" },
];

// Unified Leads + Contactos overview. Todo en vivo: leads, contactos, Empresas
// del CRM y — para administradores de plataforma — el universo de compañías de
// la plataforma con su vínculo al CRM y cuántas son Facturables (#1071).
export function ContactosLeadsDashboard() {
  const [leads, setLeads] = useState<Lead[] | null>(null);
  const [contactsTotal, setContactsTotal] = useState<number | null>(null);
  const [crmCompanies, setCrmCompanies] = useState<Company[] | null>(null);
  const [platform, setPlatform] = useState<UniverseTotals | null>(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      const [leadsRes, contactsRes] = await Promise.allSettled([
        fetchLeads({ limit: 200 }),
        fetchContacts({ limit: 1 }),
      ]);
      if (!alive) return;
      // endpoint unreachable — show "—"/empty, don't break
      setLeads(leadsRes.status === "fulfilled" ? leadsRes.value.data : []);
      setContactsTotal(contactsRes.status === "fulfilled" ? contactsRes.value.totalItems : null);
    };
    // Directorio y universo de compañías: lecturas completas, una vez al abrir
    // (no en cada sondeo de 30 s de los leads).
    Promise.allSettled([fetchCompanies({ limit: 1000 }), fetchCompaniesUniverse()]).then(([companiesRes, universeRes]) => {
      if (!alive) return;
      setCrmCompanies(companiesRes.status === "fulfilled" ? companiesRes.value.data : null);
      setPlatform(universeRes.status === "fulfilled" && universeRes.value ? universeRes.value.totals : null);
    });
    load();
    const id = setInterval(load, 30000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  const total = leads?.length ?? 0;
  const nuevos = leads?.filter((l) => l.status === "new").length ?? 0;
  const convertidos = leads?.filter((l) => l.status === "converted").length ?? 0;
  const convRate = total > 0 ? Math.round((convertidos / total) * 100) : 0;
  const clientes = crmCompanies?.filter((c) => c.stage === "customer").length ?? 0;

  // Leads por origen (live aggregation).
  const chartData: DashboardChartPoint[] = (() => {
    const order = ["Landing", "Referido", "Ads", "Evento", "Outbound", "Otro"];
    const counts: Record<string, number> = {};
    for (const name of order) counts[name] = 0;
    for (const l of leads ?? []) {
      const key = SOURCE_LABEL[(l.source ?? "").toLowerCase()] ?? "Otro";
      counts[key] = (counts[key] ?? 0) + 1;
    }
    return order.map((name) => ({ name, value: counts[name] ?? 0 }));
  })();

  const recent: RecentItem[] =
    leads && leads.length
      ? leads.slice(0, 4).map((l) => {
          const meta = STATUS_RECENT[l.status] ?? STATUS_RECENT.new;
          return {
            primary: l.name ?? "Lead sin nombre",
            secondary: [l.company, relTime(l.createdAt)].filter(Boolean).join(" · "),
            badge: meta.badge,
            tone: meta.tone,
          };
        })
      : SHORTCUT_RECENT;

  return (
    <FeatureDashboard noticeKey="crm.mockNotice"
      title="Contactos y Leads"
      subtitle="Captación de prospectos y directorio comercial en un solo lugar"
      icon="ri-contacts-book-line"
      stats={[
        { label: "Leads totales", value: leads ? String(total) : "—", icon: "ri-user-add-line" },
        { label: "Nuevos sin atender", value: leads ? String(nuevos) : "—", icon: "ri-inbox-line" },
        { label: "Tasa de conversión", value: leads ? `${convRate}%` : "—", icon: "ri-line-chart-line" },
        { label: "Contactos", value: contactsTotal === null ? "—" : String(contactsTotal), icon: "ri-user-line" },
        { label: "Empresas del CRM", value: crmCompanies ? String(crmCompanies.length) : "—", icon: "ri-building-line" },
        { label: "Clientes", value: crmCompanies ? String(clientes) : "—", icon: "ri-shake-hands-line" },
        ...(platform
          ? [
              {
                label: "Compañías de la plataforma vinculadas",
                value: `${platform.linked} / ${platform.platform}`,
                icon: "ri-links-line",
                ...(platform.unlinked ? { change: `${platform.unlinked} sin Empresa del CRM`, positive: false } : {}),
              },
              {
                label: "Facturables",
                value: String(platform.billable),
                icon: "ri-money-dollar-circle-line",
                ...(platform.needsLink ? { change: `${platform.needsLink} sin vínculo`, positive: false } : {}),
              },
            ]
          : []),
      ]}
      chart={{ title: "Leads por origen", data: chartData }}
      recent={{ title: leads && leads.length ? "Leads recientes" : "Atajos", items: recent }}
    />
  );
}

// --- Contactos -------------------------------------------------------------
// LIVE: contacts are real people in `crm_contacts`, created when a lead is
// "converted" (removed) from the Bandeja. The page also hosts the bulk import:
// imported rows are created as leads (flagged so they don't auto-call) and land
// on the Pipeline board.
export function ContactosListSection() {
  const tc = buildThemeClasses();
  const [showImport, setShowImport] = useState(false);

  return (
    <>
      <ContactosLive
        headerActions={
          <button
            onClick={() => setShowImport(true)}
            title="Importar contactos desde Excel/CSV"
            className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors hover:border-primary-500 ${tc.border} ${tc.cardBg} ${tc.text}`}
          >
            <i className="ri-file-excel-2-line text-emerald-500" />
            <span className="whitespace-nowrap">Importar</span>
          </button>
        }
      />
      {showImport && (
        <LeadsImportModal
          tc={tc}
          onClose={() => setShowImport(false)}
          onImported={() => {}}
        />
      )}
    </>
  );
}

// --- Empresas --------------------------------------------------------------
// LIVE: companies are created automatically from incoming leads and show how
// many leads in the DB are assigned to each. See EmpresasLive.
export const EmpresasSection = () => <EmpresasLive />;

// --- Segmentos -------------------------------------------------------------
interface Segmento extends Record<string, unknown> {
  id: string; nombre: string; criterio: string; contactos: string; estado: string;
}
const segmentoColumns: CrudColumn<Segmento>[] = [
  { key: "nombre", label: "Segmento" },
  { key: "criterio", label: "Criterio" },
  { key: "contactos", label: "Contactos" },
  { key: "estado", label: "Estado", align: "center", render: (r) => <StatusTextBadge value={r.estado} /> },
];
const segmentoFields: CrudField[] = [
  { key: "nombre", label: "Nombre", required: true, placeholder: "Clientes empresariales" },
  { key: "criterio", label: "Criterio", required: true, placeholder: "Tamaño > 200" },
  { key: "contactos", label: "Contactos", type: "number", placeholder: "0" },
  { key: "estado", label: "Estado", type: "select", defaultValue: "Activo", options: [
    { value: "Activo", label: "Activo" }, { value: "Inactivo", label: "Inactivo" },
  ] },
];
const segmentoSeed: Segmento[] = [
  { id: "s1", nombre: "Clientes empresariales", criterio: "Tamaño > 200", contactos: "64", estado: "Activo" },
  { id: "s2", nombre: "Leads de landing", criterio: "Origen = Landing", contactos: "128", estado: "Activo" },
  { id: "s3", nombre: "Sin actividad 30d", criterio: "Última actividad > 30 días", contactos: "47", estado: "Activo" },
];
export const SegmentosSection = () => (
  <CrudSection<Segmento> title="Segmentos" subtitle="Listas dinámicas de contactos"
    icon="ri-group-2-line" columns={segmentoColumns} fields={segmentoFields} seed={segmentoSeed} searchKey="nombre" />
);
